import { ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { ProductFlow, BoxState, ContainerType, ColumnType, useOpsParametersQuery } from 'data/__generated__';
import { QRCodeCanvas } from 'qrcode.react';
import { toast } from 'react-toastify';
import Collapsible from 'react-collapsible';
import { BsCaretDownFill, BsCaretUpFill } from 'react-icons/bs';
import { FaTimes, FaUnlink } from 'react-icons/fa';
import { SectionColumn, SectionContainer, ColumnsSectionContainer } from 'components/DetailsView/Section';
import { DetailValue } from 'components/DetailsView/DetailValue';
import { CopyValue } from 'components/CopyValue';
import { BoxProductTile } from './BoxProductTile';
import { BundleFormData, BoxProductFormData } from '../constants/types';
import { getExtendedFormattedDatetime } from 'helpers/dateTimes';
import { Loader } from 'components/Loader';
import { FilterChip, FilterChips } from 'components/FilterChip';
import { QRCodeModal } from '../../../components/QRCodeModal';
import { AddProductTile } from './AddProductTile';
import { AddProductModal } from './AddProductModal';
import { BundleAssociationTag } from './BundleAssociationTag';
import { useFormContext } from 'react-hook-form';
import { ProductSelected } from 'components/Select/ProductSelect';
import { DetailFormValue } from 'components/DetailsView';
import { formatVolume, formatWeight } from 'utils/units';
import { assertNever, returnNever } from 'utils/typing';

function computeBoxTotals(products: BoxProductFormData[]): { volumeTaken: number; weightTaken: number } {
    return products.reduce(
        (acc, product) => ({
            volumeTaken: acc.volumeTaken + (product.unitVolume || 0) * product.quantity,
            weightTaken: acc.weightTaken + (product.unitWeight || 0) * product.quantity,
        }),
        { volumeTaken: 0, weightTaken: 0 },
    );
}

export const BoxesContentForm = ({ filterBy }: { filterBy: 'position' | 'sequence' }) => {
    const {
        loading: boxOpsParametersLoading,
        data: boxOpsParametersData,
        error: boxOpsParametersError,
    } = useOpsParametersQuery();

    const formContext = useFormContext<BundleFormData>();

    const { watch, register, setValue } = formContext;

    const [isAddProductModalOpen, setIsAddProductModalOpen] = useState(false);
    const [selectedQRCode, setSelectedQRCode] = useState<{ value: string; label: string } | null>(null);
    const [selectedFlow, setSelectedFlow] = useState<ProductFlow | null>(null);

    const [selectedBox, setSelectedBox] = useState<{
        boxIndex: number;
        boxType: ProductFlow;
    } | null>(null);

    const handleOpenAddProductModal = (boxIndex: number) => {
        setSelectedBox({ boxIndex, boxType: watch(`boxes.${boxIndex}.type`) });

        setIsAddProductModalOpen(true);
    };

    const handleAddBoxProduct = (product: ProductSelected, quantity: number, quantityPicked: number | null) => {
        if (!selectedBox) return;
        const products = watch(`boxes.${selectedBox.boxIndex}.products`);

        const productExists = products.some((existingProduct) => existingProduct._id === product._id);

        if (productExists) {
            toast.warn('Ce produit est déjà présent dans cette caisse');
            return;
        }

        const unitVolume = Math.round((product.height || 0) * (product.width || 0) * (product.length || 0));
        const unitWeight = Math.round((product.weight || 0) * 1000);

        const newProducts = [
            ...products,
            {
                _id: product._id,
                photoUrl: product.imageUrl,
                productLabel: product.fullname,
                quantity,
                quantityPicked,
                unitPrice: null,
                unitVolume,
                unitWeight,
            },
        ];

        setValue(`boxes.${selectedBox.boxIndex}.products`, newProducts, { shouldDirty: true });
    };

    // Stage the box for removal from its delivery: it stays in the list with an "En cours de
    // suppression" banner (locked) and is aborted server-side on save.
    const handleAbortBox = (boxIndex: number) => {
        setValue(`boxes.${boxIndex}.state`, BoxState.Aborted, { shouldDirty: true });
    };

    // Stage the bundle detachment: the box stays in the list (and in its delivery) but every
    // bundle-related info disappears; the server detaches it from the bundle on save.
    const handleDissociateBox = (boxIndex: number) => {
        setValue(`boxes.${boxIndex}.bundleId`, null, { shouldDirty: true });
        setValue(`boxes.${boxIndex}.bundle`, null, { shouldDirty: true });
    };

    const boxes = watch('boxes');

    if (!boxes || boxes.length === 0) {
        return (
            <SectionContainer title="Caisses" isInitiallyOpen>
                <EmptyMessage>Aucune caisse associée</EmptyMessage>
            </SectionContainer>
        );
    }

    if (boxOpsParametersLoading) {
        return (
            <SectionContainer title={`Caisses (${boxes.length})`} isInitiallyOpen>
                <Loader />
            </SectionContainer>
        );
    }

    if (boxOpsParametersError || !boxOpsParametersData) {
        return (
            <SectionContainer title={`Caisses (${boxes.length})`} isInitiallyOpen>
                <EmptyMessage>Une erreur est survenue lors de la récupération des paramètres ops</EmptyMessage>
            </SectionContainer>
        );
    }

    const { opsParameters } = boxOpsParametersData;
    const parameters = opsParameters.parameters;

    function getVolumeCapacity({
        type,
        containerType,
    }: {
        type: ProductFlow | null;
        containerType: ContainerType | null;
    }) {
        if (!type) return '?';
        let value = 0;
        if (containerType === ContainerType.Palette) {
            value = parameters.volumePaletteBox;
        } else if (containerType === ContainerType.Kraft) {
            value = parameters.volumeKraftBox;
        } else {
            if (!type) return '?';
            const flowParameter = parameters.flowParameters.find((fp) => fp.flow === type);
            if (!flowParameter) return '?';
            value = flowParameter.volumeBox;
        }
        return formatVolume(value);
    }

    function getWeightCapacity({
        type,
        containerType,
    }: {
        type: ProductFlow | null;
        containerType: ContainerType | null;
    }) {
        if (!type) return '?';
        let value = 0;
        if (containerType === ContainerType.Palette) {
            value = parameters.weightPaletteBox;
        } else if (containerType === ContainerType.Kraft) {
            value = parameters.weightKraftBox;
        } else {
            if (!type) return '?';
            const flowParameter = parameters.flowParameters.find((fp) => fp.flow === type);
            if (!flowParameter) return '?';
            value = flowParameter.weightBox;
        }
        return formatWeight(value);
    }

    const boxCountByFlow = boxes.reduce<Partial<Record<ProductFlow, number>>>((acc, box) => {
        if (box.type) {
            acc[box.type] = (acc[box.type] ?? 0) + 1;
        }
        return acc;
    }, {});
    const flowsPresent = Object.values(ProductFlow).filter((flow) => boxCountByFlow[flow]);
    // Fall back to "all" if the selected flow no longer has any box (e.g. last one deleted)
    const activeFlow = selectedFlow && boxCountByFlow[selectedFlow] ? selectedFlow : null;

    return (
        <SectionContainer title={`Caisses (${boxes.length})`} isInitiallyOpen>
            <>
                {flowsPresent.length > 1 && (
                    <FilterChips>
                        <FilterChip type="button" $isActive={activeFlow === null} onClick={() => setSelectedFlow(null)}>
                            Tous ({boxes.length})
                        </FilterChip>
                        {flowsPresent.map((flow) => (
                            <FilterChip
                                key={flow}
                                type="button"
                                $isActive={activeFlow === flow}
                                onClick={() => setSelectedFlow(flow)}
                            >
                                {getTranslatedType(flow)} ({boxCountByFlow[flow]})
                            </FilterChip>
                        ))}
                    </FilterChips>
                )}
                <ColumnsSectionContainer numberOfColumns={1}>
                    <SectionColumn>
                        {boxes
                            .map((box, boxIndex) => ({ ...box, boxIndex }))
                            .filter((box) => activeFlow === null || box.type === activeFlow)
                            .sort((boxA, boxB) =>
                                filterBy === 'position'
                                    ? (boxA.position || 0) - (boxB.position || 0)
                                    : (boxA.sequence || 0) - (boxB.sequence || 0),
                            )
                            .map((box) => {
                                const {
                                    _id: boxId,
                                    createdAt,
                                    code,
                                    deliveryCode,
                                    bundle,
                                    bundleId,
                                    state,
                                    number,
                                    position,
                                    products,
                                    type,
                                    containerType,
                                    volumeTaken,
                                    weightTaken,
                                    column,
                                    columnId,
                                    boxIndex,
                                } = box;
                                const boxVolume = getVolumeCapacity({ type, containerType });
                                const boxWeight = getWeightCapacity({ type, containerType });

                                const computed = computeBoxTotals(products);
                                const displayVolume = computed.volumeTaken || volumeTaken || 0;
                                const displayWeight = computed.weightTaken || weightTaken || 0;

                                // Bundle page (sorted by position) → position in the bundle;
                                // delivery page → box number in the delivery.
                                const boxNumber = filterBy === 'position' ? position : number;

                                // Staged "retirer de la livraison": the box stays listed but locked.
                                const isPendingRemoval = state === BoxState.Aborted;
                                // Bundle page only: a box staged for detachment (bundleId nulled) leaves
                                // this bundle on save → same staged treatment as the removal.
                                const isPendingDissociation = filterBy === 'position' && !bundleId && !isPendingRemoval;
                                const isLocked = isPendingRemoval || isPendingDissociation;

                                return (
                                    <BoxContainer key={boxId}>
                                        {isPendingRemoval && (
                                            <PendingRemovalBanner>
                                                En cours de suppression — la caisse sera retirée de la livraison à
                                                l'enregistrement
                                            </PendingRemovalBanner>
                                        )}
                                        {isPendingDissociation && (
                                            <PendingRemovalBanner>
                                                En cours de désassociation — la caisse sera retirée du bundle à
                                                l'enregistrement
                                            </PendingRemovalBanner>
                                        )}
                                        <LockableContent disabled={isLocked} $isLocked={isLocked}>
                                            <BoxInfo
                                                title={
                                                    <Title>
                                                        {boxNumber !== null && boxNumber !== undefined
                                                            ? `#${boxNumber} - `
                                                            : ''}
                                                        {getContainerType(containerType)} - {getTranslatedType(type)}
                                                        {column ? ` - ${column.name}` : ''}
                                                        {deliveryCode && (
                                                            <QRCodeButton
                                                                type="button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setSelectedQRCode({
                                                                        value: deliveryCode,
                                                                        label: 'Code de livraison',
                                                                    });
                                                                }}
                                                            >
                                                                <QRCodeCanvas value={deliveryCode} size={20} />
                                                            </QRCodeButton>
                                                        )}
                                                    </Title>
                                                }
                                                actions={
                                                    isLocked ? null : (
                                                        <>
                                                            {bundleId ? (
                                                                <DissociateBoxButton
                                                                    type="button"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleDissociateBox(boxIndex);
                                                                    }}
                                                                    aria-label="Retirer du bundle"
                                                                    title="Retirer du bundle"
                                                                >
                                                                    <FaUnlink />
                                                                </DissociateBoxButton>
                                                            ) : null}
                                                            <DeleteBoxButton
                                                                type="button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleAbortBox(boxIndex);
                                                                }}
                                                                aria-label="Retirer de la livraison"
                                                                title="Retirer de la livraison"
                                                            >
                                                                <FaTimes />
                                                            </DeleteBoxButton>
                                                        </>
                                                    )
                                                }
                                            >
                                                <Details>
                                                    <DetailsColumn>
                                                        <CopyValue label="ID" value={boxId} />
                                                        <DetailValue
                                                            label="Volume occupé"
                                                            value={`${formatVolume(displayVolume)} / ${boxVolume}`}
                                                        />
                                                        <DetailValue
                                                            label="Créée"
                                                            value={
                                                                createdAt
                                                                    ? getExtendedFormattedDatetime(createdAt)
                                                                    : '?'
                                                            }
                                                        />
                                                        {position !== null && position !== undefined && (
                                                            <DetailValue label="Position" value={position} />
                                                        )}
                                                        <CodeContainer>
                                                            <DetailFormValue
                                                                label="Code de picking"
                                                                placeholder="Code inexistant"
                                                                {...register(`boxes.${boxIndex}.code`)}
                                                            />
                                                            {code && (
                                                                <QRCodeButton
                                                                    type="button"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setSelectedQRCode({
                                                                            value: code,
                                                                            label: 'Code de picking',
                                                                        });
                                                                    }}
                                                                >
                                                                    <QRCodeCanvas value={code} size={40} />
                                                                </QRCodeButton>
                                                            )}
                                                        </CodeContainer>
                                                    </DetailsColumn>
                                                    <DetailsColumn>
                                                        {column && (
                                                            <DetailValue
                                                                label="Colonne de destination"
                                                                value={
                                                                    columnId ? (
                                                                        <ColumnLink
                                                                            to={
                                                                                column.type ===
                                                                                ColumnType.VirtualMicrostore
                                                                                    ? `/column/${columnId}/stocks`
                                                                                    : `/microstore-column/${columnId}`
                                                                            }
                                                                        >
                                                                            {column.fullname}
                                                                        </ColumnLink>
                                                                    ) : (
                                                                        column.fullname
                                                                    )
                                                                }
                                                            />
                                                        )}
                                                        <DetailValue
                                                            label="Poids"
                                                            value={`${formatWeight(displayWeight)} / ${boxWeight}`}
                                                        />
                                                        <DetailValue
                                                            label="Préparée"
                                                            value={
                                                                bundle?.closingDate
                                                                    ? getExtendedFormattedDatetime(bundle.closingDate)
                                                                    : 'Pas encore préparée'
                                                            }
                                                        />
                                                        {bundle?._id && (
                                                            <DetailValue
                                                                label="Bundle"
                                                                value={
                                                                    <BundleAssociationTag
                                                                        bundleId={bundle._id}
                                                                        bundleNumber={bundle.number}
                                                                        state={bundle.state}
                                                                    />
                                                                }
                                                            />
                                                        )}
                                                    </DetailsColumn>
                                                </Details>
                                            </BoxInfo>
                                            <Products>
                                                {products.map((product, productIndex) => (
                                                    <BoxProductTile
                                                        key={`${boxIndex}-${productIndex}-${product._id}`}
                                                        boxIndex={boxIndex}
                                                        productIndex={productIndex}
                                                    />
                                                ))}
                                                <AddProductTile
                                                    onClick={() => handleOpenAddProductModal(boxIndex)}
                                                    boxId={boxId}
                                                />
                                            </Products>
                                        </LockableContent>
                                    </BoxContainer>
                                );
                            })}
                    </SectionColumn>
                </ColumnsSectionContainer>

                {selectedQRCode && (
                    <QRCodeModal
                        isOpen={!!selectedQRCode}
                        onClose={() => setSelectedQRCode(null)}
                        value={selectedQRCode.value}
                        showValue
                        valueLabel={selectedQRCode.label}
                    />
                )}

                <AddProductModal
                    isOpen={isAddProductModalOpen}
                    onClose={() => setIsAddProductModalOpen(false)}
                    onAddProduct={handleAddBoxProduct}
                    boxFlow={selectedBox?.boxType}
                />
            </>
        </SectionContainer>
    );
};

function getTranslatedType(type: ProductFlow | null) {
    if (type === undefined || type === null) return 'Non précisé';

    switch (type) {
        case ProductFlow.Bakery:
            return 'Boulangerie';
        case ProductFlow.Dry:
            return 'Sec';
        case ProductFlow.Fresh:
            return 'Frais';
        case ProductFlow.Fruit:
            return 'Fruits';
        case ProductFlow.Caterer:
            return 'Traiteur';
        case ProductFlow.Kumo:
            return 'Kumo';
        default:
            return returnNever(type);
    }
}

function getContainerType(type: ContainerType | null): string {
    if (type === undefined || type === null) return 'Non précisé';

    switch (type) {
        case ContainerType.Box:
            return 'Caisse';
        case ContainerType.Kangabox:
            return 'Kangabox';
        case ContainerType.FreshParcel:
            return 'Colis frais';
        case ContainerType.Parcel:
            return 'Colis';
        case ContainerType.Kraft:
            return 'Kraft';
        case ContainerType.Palette:
            return 'Palette';
        default:
            return assertNever(type);
    }
}

const BoxInfo = ({ title, actions, children }: { title: JSX.Element; actions?: ReactNode; children: JSX.Element }) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <Collapsible
            trigger={
                <BoxHeader onClick={() => setIsOpen((open) => !open)}>
                    <CaretIcon>{isOpen ? <BsCaretUpFill size={14} /> : <BsCaretDownFill size={14} />}</CaretIcon>
                    {title}
                    {actions ? <BoxActions>{actions}</BoxActions> : null}
                </BoxHeader>
            }
            open={isOpen}
            transitionTime={150}
            overflowWhenOpen="visible"
        >
            {children}
        </Collapsible>
    );
};

const BoxContainer = styled.div`
    width: 100%;
    position: relative;
`;

const BoxHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding-right: 70px;
    cursor: pointer;
    width: fit-content;
`;

const BoxActions = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    display: flex;
    align-items: center;
    gap: 4px;
`;

const PendingRemovalBanner = styled.div`
    border: 1px solid ${({ theme }) => theme.errorColor};
    background-color: ${({ theme }) => `${theme.errorColor}15`};
    color: ${({ theme }) => theme.errorColor};
    font-weight: 700;
    border-radius: ${({ theme }) => theme.borderRadius};
    padding: 8px 12px;
    margin-bottom: 8px;
`;

// fieldset so every nested input is natively disabled; pointer-events kills the remaining
// click handlers (QR codes, add-product tile, collapse toggle) while the box awaits removal.
const LockableContent = styled.fieldset<{ $isLocked: boolean }>`
    border: none;
    padding: 0;
    margin: 0;
    min-inline-size: auto;

    ${({ $isLocked }) => ($isLocked ? 'pointer-events: none; opacity: 0.55;' : '')}
`;

const CaretIcon = styled.span`
    display: inline-flex;
    align-items: center;
    color: ${({ theme }) => theme.textColor};
`;

const Title = styled.span`
    font-size: 22px;
    font-weight: 800;
    color: ${({ theme }) => theme.textColor};
`;

const DeleteBoxButton = styled.button`
    background: none;
    border: none;
    color: ${({ theme }) => theme.errorColor};
    cursor: pointer;
    padding: 4px;
    font-size: 16px;
    border-radius: ${({ theme }) => theme.borderRadius};
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
        background-color: ${({ theme }) => `${theme.errorColor}20`};
    }
`;

const DissociateBoxButton = styled.button`
    background: none;
    border: none;
    color: ${({ theme }) => theme.ctaPrimaryColor};
    cursor: pointer;
    padding: 4px;
    font-size: 14px;
    border-radius: ${({ theme }) => theme.borderRadius};
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
        background-color: ${({ theme }) => `${theme.ctaPrimaryColor}20`};
    }
`;
const Details = styled.div`
    border-left: 3px solid ${({ theme }) => theme.lightBorderColor};
    padding-left: 10px;
    margin-top: 10px;
    display: flex;
    gap: 15px;
`;

const DetailsColumn = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 10px;
`;
const Products = styled.div`
    display: grid;
    gap: 10px;
    margin-top: 10px;

    @media screen {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    @media print {
        grid-template-columns: 1fr;
    }
`;
const EmptyMessage = styled.div`
    color: ${({ theme }) => theme.textColor};
    font-size: 16px;
    padding: 20px;
    text-align: center;
`;

const QRCodeButton = styled.button`
    padding: 0;
    border: none;
    background-color: white;
    cursor: pointer;
    margin-left: 10px;
    width: 20px;
    height: 20px;
    border-radius: 4px;
    &:hover {
        opacity: 0.8;
    }
`;

const ColumnLink = styled(Link)`
    color: ${({ theme }) => theme.ctaPrimaryTextColor};
    text-decoration: none;
    &:hover {
        text-decoration: underline;
    }
`;

const CodeContainer = styled.div`
    display: flex;
    align-items: center;

    & > :first-child {
        flex: 1;
    }

    & > :not(:first-child) {
        margin-left: 10px;
    }
`;
